import { useEffect, useState } from "react";
import type { AgentDTO, SkillDetailDTO, SkillDTO } from "../../shared/types";
import { formatBytes } from "./Files";
import { List, ListRow } from "./List";
import { MultiSelect } from "./MultiSelect";
import { agentsForPersonSkills, InactiveNote, inactiveAgentIds, LevelTag, SkillWho } from "./SkillWho";
import { confirmDialog } from "./Dialog";
import { Badge, Button, ErrorText, timeAgo } from "./ui";
import { get, patch } from "../lib/api";

const MAIN = "SKILL.md";

/** The skill as it is written: SKILL.md first and whole, then what else is in the folder. Read-only on purpose. */
function SkillBody({ skillId }: { skillId: string }) {
  const [skill, setSkill] = useState<SkillDetailDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    get<SkillDetailDTO>(`/api/skills/${skillId}`).then(setSkill, (e) => setError(e.message));
  }, [skillId]);

  if (error) return <ErrorText error={error} />;
  if (!skill) return <p className="mt-3 text-sm text-muted">Loading…</p>;
  const main = skill.files.find((f) => f.path === MAIN);
  const others = skill.files.filter((f) => f.path !== MAIN);
  return (
    <div className="mt-3 space-y-3">
      <pre className="scroll-thin max-h-80 overflow-auto rounded-xl bg-fill-subtle p-3 text-xs whitespace-pre-wrap">{main?.text ?? "(not text)"}</pre>
      {others.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted">
          {others.map((f) => (
            <li key={f.path} className="font-mono">
              {f.path} · {formatBytes(f.size)}
              {f.executable ? " · executable" : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** One skill of this person's: what it is for, which agents follow it, how often it was loaded, and Archive. */
function SkillRow({ skill, agents, onChanged }: { skill: SkillDTO; agents: AgentDTO[]; onChanged: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The agents this skill may be on, plus any it is on and no longer reaches, so one left behind can still be taken
  // off. Nothing else is offered: the platform would refuse it.
  const inactive = inactiveAgentIds(skill, agents);
  const options = [
    ...agentsForPersonSkills(agents, skill.userId).map((a) => ({ value: a.id, label: a.name, hint: a.slug })),
    ...inactive.map((id) => ({ value: id, label: agents.find((a) => a.id === id)?.name ?? "an agent", hint: "inactive" })),
  ];
  const save = async (body: Record<string, unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await patch(`/api/skills/${skill.id}`, body);
      await onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <ListRow
      title={
        <>
          <span className="font-mono text-sm">{skill.name}</span>
          <Badge>v{skill.version}</Badge>
          <LevelTag level={skill.level} />
          {skill.archivedAt ? <Badge tone="amber">archived</Badge> : !skill.offered && <Badge tone="amber">not offered</Badge>}
        </>
      }
      trailing={
        <>
          <Button variant="ghost" size="sm" onClick={() => setOpen(!open)}>
            {open ? "Hide" : "Read"}
          </Button>
          {!skill.archivedAt && (
            <Button
              variant="danger"
              size="sm"
              disabled={busy}
              onClick={async () => (await confirmDialog({ title: `Stop offering "${skill.name}"?`, body: "It is kept, and no agent is given it again.", confirm: "Archive" })) && save({ archived: true })}
            >
              Archive
            </Button>
          )}
        </>
      }
    >
      <p className="mt-1 text-sm text-muted">{skill.description}</p>
      <div className="mt-1.5 text-xs text-faint">
        {skill.fileCount} {skill.fileCount === 1 ? "file" : "files"} · {formatBytes(skill.size)} · {skill.reads} {skill.reads === 1 ? "load" : "loads"}
        {skill.lastReadAt ? `, last ${timeAgo(skill.lastReadAt)}` : ""} · written {timeAgo(skill.createdAt)}
        {skill.archivedAt ? ` · archived ${timeAgo(skill.archivedAt)}` : !skill.offered ? " · no agent has it, so no run is offered it" : ""}
      </div>
      {!skill.archivedAt && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted">Used by</span>
            <SkillWho skill={skill} agents={agents} person={false} mine />
          </div>
          <div className={busy ? "pointer-events-none opacity-60" : undefined}>
            <MultiSelect
              options={options}
              selected={skill.grantedAgentIds}
              onChange={(agentIds) => void save({ agentIds })}
              placeholder="Choose agents"
              all="All your agents"
              summary={(chosen) => (chosen.length ? chosen.map((o) => o.label).join(", ") : "No agent has this skill yet")}
            />
          </div>
          <InactiveNote skill={skill} agents={agents} mine />
        </div>
      )}
      {open && <SkillBody skillId={skill.id} />}
      <ErrorText error={error} />
    </ListRow>
  );
}

/**
 * "My skills": the procedures agents follow for this one person. Only runs working for them are ever given these,
 * so nobody else's work changes when one is written, and they are the person's to share, to stop sharing and to
 * stop offering. An agent an item is shared with follows it even when another agent hands it the work.
 */
export function MySkills({ userId }: { userId: string }) {
  const [skills, setSkills] = useState<SkillDTO[] | null>(null);
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [error, setError] = useState<string | null>(null);
  const load = async () => {
    try {
      setSkills(await get<SkillDTO[]>(`/api/skills/person/${userId}`));
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => {
    void load();
  }, [userId]);
  useEffect(() => {
    get<AgentDTO[]>("/api/agents").then(setAgents, () => {});
  }, []);

  return (
    <div className="space-y-6">
      <ErrorText error={error} />
      {skills && (
        <List
          title="My skills"
          hint="Procedures an agent follows for you alone. Only runs working for you are given them, on the agents you share them with — hand-offs included. You can share them with the agents that take people's skills and with the agents you own."
          empty={'None yet. Tell an agent how you want a recurring job done — "when I ask for the weekly report, always…" — and ask it to write that down as a skill for you.'}
        >
          {skills.map((s) => (
            <SkillRow key={s.id} skill={s} agents={agents} onChanged={load} />
          ))}
        </List>
      )}
    </div>
  );
}
